export interface StaticPage {
  id: string;
  title: string;
  path: string;
  description: string;
  content: string;
  lastUpdated: string;
  category: 'legal' | 'company' | 'support' | 'help';
  external?: boolean;
  externalUrl?: string;
}

export interface StaticPageCategory {
  id: string;
  name: string;
  description: string;
  pages: StaticPage[];
}

export const STATIC_PAGES: StaticPage[] = [
  // Legal Pages
  {
    id: 'privacy-policy',
    title: 'Privacy Policy',
    path: '/privacy',
    description: 'How we collect, use, and protect your personal information',
    content: `
      <h1>Privacy Policy</h1>
      <p>Last updated: ${new Date().toLocaleDateString()}</p>
      
      <h2>1. Information We Collect</h2>
      <p>We collect information you provide directly to us, such as when you create an account, 
      update your profile, or contact us for support.</p>
      
      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to provide, maintain, and improve our services, 
      communicate with you, and ensure security.</p>
      
      <h2>3. Information Sharing</h2>
      <p>We do not sell, trade, or otherwise transfer your personal information to third parties 
      without your consent, except as described in this policy.</p>
      
      <h2>4. Data Security</h2>
      <p>We implement appropriate security measures to protect your personal information 
      against unauthorized access, alteration, disclosure, or destruction.</p>
      
      <h2>5. Your Rights</h2>
      <p>You have the right to access, update, or delete your personal information. 
      Contact us to exercise these rights.</p>
      
      <h2>6. Contact Us</h2>
      <p>If you have questions about this Privacy Policy, please contact us at 
      <a href="mailto:privacy@example.com">privacy@example.com</a>.</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'legal'
  },
  
  {
    id: 'terms-of-service',
    title: 'Terms of Service',
    path: '/terms',
    description: 'Terms and conditions for using our services',
    content: `
      <h1>Terms of Service</h1>
      <p>Last updated: ${new Date().toLocaleDateString()}</p>
      
      <h2>1. Acceptance of Terms</h2>
      <p>By accessing and using this service, you accept and agree to be bound by the terms 
      and provision of this agreement.</p>
      
      <h2>2. Use License</h2>
      <p>Permission is granted to temporarily download one copy of the materials on this 
      service for personal, non-commercial transitory viewing only.</p>
      
      <h2>3. Disclaimer</h2>
      <p>The materials on this service are provided on an 'as is' basis. We make no 
      warranties, expressed or implied, and hereby disclaim and negate all other warranties 
      including without limitation, implied warranties or conditions of merchantability, 
      fitness for a particular purpose, or non-infringement of intellectual property.</p>
      
      <h2>4. Limitations</h2>
      <p>In no event shall we or our suppliers be liable for any damages arising out of the 
      use or inability to use the materials on this service.</p>
      
      <h2>5. Revisions and Errata</h2>
      <p>The materials appearing on this service could include technical, typographical, 
      or photographic errors. We do not warrant that any of the materials are accurate, 
      complete or current.</p>
      
      <h2>6. Links</h2>
      <p>We have not reviewed all of the sites linked to this service and are not 
      responsible for the contents of any such linked site.</p>
      
      <h2>7. Modifications</h2>
      <p>We may revise these terms of service at any time without notice. By using this 
      service you are agreeing to be bound by the then current version of these Terms of Service.</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'legal'
  },
  
  {
    id: 'cookie-policy',
    title: 'Cookie Policy',
    path: '/cookies',
    description: 'How we use cookies and similar technologies',
    content: `
      <h1>Cookie Policy</h1>
      <p>Last updated: ${new Date().toLocaleDateString()}</p>
      
      <h2>1. What Are Cookies</h2>
      <p>Cookies are small text files that are placed on your device when you visit our website. 
      They help us provide you with a better experience and understand how you use our site.</p>
      
      <h2>2. Types of Cookies We Use</h2>
      <ul>
        <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
        <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site</li>
        <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
        <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
      </ul>
      
      <h2>3. Managing Cookies</h2>
      <p>You can control and manage cookies through your browser settings. However, 
      disabling certain cookies may affect the functionality of our website.</p>
      
      <h2>4. Third-Party Cookies</h2>
      <p>We may use third-party services that also use cookies. These services have 
      their own privacy policies and cookie policies.</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'legal'
  },
  
  // Company Pages
  {
    id: 'about-us',
    title: 'About Us',
    path: '/about',
    description: 'Learn more about our company and mission',
    content: `
      <h1>About Us</h1>
      
      <h2>Our Mission</h2>
      <p>We are dedicated to providing innovative CRM solutions that help businesses 
      grow and succeed in today's competitive market.</p>
      
      <h2>Our Story</h2>
      <p>Founded with a vision to simplify customer relationship management, we've been 
      helping businesses of all sizes streamline their operations and improve customer satisfaction.</p>
      
      <h2>Our Values</h2>
      <ul>
        <li><strong>Innovation:</strong> We constantly strive to improve and innovate</li>
        <li><strong>Customer Focus:</strong> Our customers' success is our success</li>
        <li><strong>Integrity:</strong> We operate with honesty and transparency</li>
        <li><strong>Excellence:</strong> We deliver the highest quality solutions</li>
      </ul>
      
      <h2>Our Team</h2>
      <p>Our team consists of experienced professionals passionate about technology 
      and customer success. We work together to deliver exceptional value to our customers.</p>
      
      <h2>Contact Information</h2>
      <p>For more information about our company, please contact us at 
      <a href="mailto:info@example.com">info@example.com</a>.</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'company'
  },
  
  {
    id: 'contact-us',
    title: 'Contact Us',
    path: '/contact',
    description: 'Get in touch with our team',
    content: `
      <h1>Contact Us</h1>
      
      <h2>Get in Touch</h2>
      <p>We're here to help! Reach out to us through any of the following channels:</p>
      
      <h3>Email Support</h3>
      <p><strong>General Inquiries:</strong> <a href="mailto:info@example.com">info@example.com</a></p>
      <p><strong>Technical Support:</strong> <a href="mailto:support@example.com">support@example.com</a></p>
      <p><strong>Sales:</strong> <a href="mailto:sales@example.com">sales@example.com</a></p>
      
      <h3>Phone Support</h3>
      <p><strong>Main Office:</strong> +1 (555) 123-4567</p>
      <p><strong>Support Hotline:</strong> +1 (555) 987-6543</p>
      
      <h3>Office Hours</h3>
      <p><strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM EST</p>
      <p><strong>Saturday:</strong> 10:00 AM - 4:00 PM EST</p>
      <p><strong>Sunday:</strong> Closed</p>
      
      <h3>Address</h3>
      <p>123 Business Street<br>
      Suite 100<br>
      City, State 12345<br>
      United States</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'company'
  },
  
  // Support Pages
  {
    id: 'help-center',
    title: 'Help Center',
    path: '/help',
    description: 'Find answers to common questions and get support',
    content: `
      <h1>Help Center</h1>
      
      <h2>Frequently Asked Questions</h2>
      
      <h3>Getting Started</h3>
      <details>
        <summary>How do I create an account?</summary>
        <p>Click the "Sign Up" button on the login page and follow the registration process. 
        You'll need to provide your email address and create a password.</p>
      </details>
      
      <details>
        <summary>How do I reset my password?</summary>
        <p>Click "Forgot Password" on the login page and enter your email address. 
        We'll send you a link to reset your password.</p>
      </details>
      
      <h3>Using the Platform</h3>
      <details>
        <summary>How do I add a new customer?</summary>
        <p>Navigate to the Customers section and click "Add Customer". Fill in the required 
        information and save.</p>
      </details>
      
      <details>
        <summary>How do I create a deal?</summary>
        <p>Go to the Deals section and click "New Deal". Enter the deal details including 
        customer, value, and expected close date.</p>
      </details>
      
      <h3>Account Management</h3>
      <details>
        <summary>How do I update my profile?</summary>
        <p>Click on your profile picture in the top right corner and select "Profile" 
        to edit your information.</p>
      </details>
      
      <details>
        <summary>How do I change my password?</summary>
        <p>Go to your profile settings and click "Change Password" to update your password.</p>
      </details>
      
      <h2>Still Need Help?</h2>
      <p>If you can't find the answer you're looking for, please contact our support team:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:support@example.com">support@example.com</a></li>
        <li><strong>Phone:</strong> +1 (555) 987-6543</li>
        <li><strong>Live Chat:</strong> Available during business hours</li>
      </ul>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'support'
  },
  
  {
    id: 'support',
    title: 'Support',
    path: '/support',
    description: 'Get technical support and assistance',
    content: `
      <h1>Support</h1>
      
      <h2>Technical Support</h2>
      <p>Our technical support team is here to help you with any issues you may encounter 
      while using our platform.</p>
      
      <h3>Support Channels</h3>
      <ul>
        <li><strong>Email Support:</strong> <a href="mailto:support@example.com">support@example.com</a></li>
        <li><strong>Phone Support:</strong> +1 (555) 987-6543</li>
        <li><strong>Live Chat:</strong> Available in the bottom right corner</li>
        <li><strong>Knowledge Base:</strong> <a href="/help">Help Center</a></li>
      </ul>
      
      <h3>Response Times</h3>
      <ul>
        <li><strong>Critical Issues:</strong> Within 2 hours</li>
        <li><strong>High Priority:</strong> Within 4 hours</li>
        <li><strong>Medium Priority:</strong> Within 24 hours</li>
        <li><strong>Low Priority:</strong> Within 48 hours</li>
      </ul>
      
      <h2>Before Contacting Support</h2>
      <p>To help us assist you more quickly, please have the following information ready:</p>
      <ul>
        <li>Your account email address</li>
        <li>Description of the issue</li>
        <li>Steps to reproduce the problem</li>
        <li>Screenshots (if applicable)</li>
        <li>Browser and operating system information</li>
      </ul>
      
      <h2>Business Hours</h2>
      <p><strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM EST<br>
      <strong>Saturday:</strong> 10:00 AM - 4:00 PM EST<br>
      <strong>Sunday:</strong> Closed</p>
      
      <h2>Emergency Support</h2>
      <p>For critical issues outside of business hours, please email 
      <a href="mailto:emergency@example.com">emergency@example.com</a>.</p>
    `,
    lastUpdated: new Date().toISOString(),
    category: 'support'
  }
];

export const STATIC_PAGE_CATEGORIES: StaticPageCategory[] = [
  {
    id: 'legal',
    name: 'Legal',
    description: 'Legal documents and policies',
    pages: STATIC_PAGES.filter(page => page.category === 'legal')
  },
  {
    id: 'company',
    name: 'Company',
    description: 'Information about our company',
    pages: STATIC_PAGES.filter(page => page.category === 'company')
  },
  {
    id: 'support',
    name: 'Support',
    description: 'Help and support resources',
    pages: STATIC_PAGES.filter(page => page.category === 'support')
  }
];

// Utility functions
export const getStaticPage = (id: string): StaticPage | undefined => {
  return STATIC_PAGES.find(page => page.id === id);
};

export const getStaticPageByPath = (path: string): StaticPage | undefined => {
  return STATIC_PAGES.find(page => page.path === path);
};

export const getStaticPagesByCategory = (category: string): StaticPage[] => {
  return STATIC_PAGES.filter(page => page.category === category);
};

export const getAllStaticPages = (): StaticPage[] => {
  return STATIC_PAGES;
};

export const getStaticPageCategories = (): StaticPageCategory[] => {
  return STATIC_PAGE_CATEGORIES;
}; 